import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { FINDER_PROBLEMS, SERVICES } from "../data/services";
import { SectionHeader } from "./SectionHeader";
import { IMG, imgProps } from "../data/assets";
import { track } from "../lib/analytics";

// Real DCC photography per problem → service mapping (approved project assets;
// intrinsic dimensions come from the generated image manifest — no stock imagery).
const FINDER_IMG = {
  "air-duct": "plantRobot",
  "kitchen-exhaust": "kitchenPro",
  "laundry-exhaust": "laundryOps",
  "drain-grease": "greaseDrain",
  "dry-ice": "dryIce",
  unsure: "facilitySurvey",
};

// Shared copy + CTAs for the recommendation (desktop overlay + mobile card).
function FinderContent({ selected, service, surveyHref, onReset, mobile = false }) {
  const suffix = mobile ? "-mobile" : "";
  if (!selected) {
    return (
      <>
        <p className="eyebrow-dark">Find the right service</p>
        <h3 className="h-display mt-4 text-2xl text-zinc-50 sm:text-3xl">Tell us what you are experiencing</h3>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-300">
          Every DCC engagement starts from the symptom, not the sales catalogue. Your answer maps to the specialist system that fixes it.
        </p>
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-dcc">
          Select one problem to see the recommended service →
        </p>
      </>
    );
  }
  return (
    <>
      <p className="eyebrow-dark">Recommended {service ? "service" : "next step"}</p>
      <h3 className="h-display mt-4 text-2xl text-zinc-50 sm:text-3xl">
        {service ? service.name : "Technical Site Survey"}
      </h3>
      <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-300">{selected.note}</p>
      <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-4">
        {service && (
          <Link to={`/services/${service.slug}`} data-testid={`finder-service-link${suffix}`}
            onClick={() => track("explore_service_click", { service: service.slug, placement: mobile ? "service_finder_mobile" : "service_finder" })}
            className="link-tech min-h-[44px] text-zinc-100">
            Explore service <span aria-hidden="true">→</span>
          </Link>
        )}
        <Link to={surveyHref} data-testid={`finder-survey-cta${suffix}`} className="btn-primary !py-3.5 text-xs">
          Request a Free Site Survey <ArrowUpRight size={15} aria-hidden="true" />
        </Link>
        <button type="button" data-testid={`finder-reset-btn${suffix}`} onClick={onReset} className="link-tech min-h-[44px] text-zinc-400">
          <RotateCcw size={13} aria-hidden="true" /> Reset
        </button>
      </div>
    </>
  );
}

export function ServiceFinder() {
  const [selected, setSelected] = useState(null);
  const service = selected?.service ? SERVICES.find((s) => s.id === selected.service) : null;
  const imgMeta = IMG[FINDER_IMG[selected ? (selected.service || "unsure") : "unsure"]];

  const select = (p) => {
    setSelected(p);
    const svc = SERVICES.find((s) => s.id === p.service);
    track("service_finder_select", { problem: p.id, service: svc ? svc.slug : "needs-assessment" });
  };

  const surveyHref = selected
    ? `/request-survey?problem=${selected.id}${service ? `&service=${service.slug}` : ""}&source=service-finder`
    : "/request-survey";

  return (
    <section className="noise-dark relative overflow-hidden text-zinc-100" aria-labelledby="finder-heading">
      <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
      <div className="container-x relative py-16 md:py-24">
        <SectionHeader no="01" eyebrow="Find the right service" title="Start with the problem, not the terminology." dark />
        <div className="grid gap-10 lg:grid-cols-12">
          {/* left — selectable problem list */}
          <div className="lg:col-span-5" role="group" aria-label="Select your facility problem">
            {FINDER_PROBLEMS.map((p) => {
              const isSel = selected?.id === p.id;
              return (
                <div key={p.id}>
                  <button
                    type="button"
                    data-testid={`finder-problem-${p.id}`}
                    onClick={() => select(p)}
                    aria-pressed={isSel}
                    aria-expanded={isSel}
                    aria-controls={`finder-inline-${p.id}`}
                    className={`relative flex min-h-[64px] w-full items-center justify-between gap-4 border-b py-5 pl-5 text-left transition-colors duration-200 ${
                      isSel ? "border-dcc text-dcc" : "border-zinc-800 text-zinc-200 hover:border-zinc-600 hover:text-zinc-50"
                    }`}
                  >
                    <span aria-hidden="true" className={`absolute left-0 top-0 h-full w-[3px] transition-colors duration-200 ${isSel ? "bg-dcc" : "bg-transparent"}`} />
                    <span className="font-display text-lg font-medium uppercase tracking-tight sm:text-xl">{p.label}</span>
                    <span className={`shrink-0 font-mono text-xs ${isSel ? "text-dcc" : "text-zinc-400"}`} aria-hidden="true">
                      {isSel ? "●" : "○"}
                    </span>
                  </button>
                  {/* mobile — selected image + recommendation directly below the problem */}
                  <AnimatePresence initial={false}>
                    {isSel && (
                      <motion.div
                        id={`finder-inline-${p.id}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden lg:hidden"
                      >
                        <div className="border border-zinc-800" data-testid="finder-result-mobile">
                          <div className="relative overflow-hidden">
                            <img
                              src={imgMeta.src}
                              alt={imgMeta.alt}
                              {...imgProps(imgMeta.src)}
                              loading="lazy" decoding="async"
                              style={{ objectPosition: imgMeta.pos }}
                              className="aspect-[16/10] w-full object-cover"
                            />
                          </div>
                          <div className="bg-zinc-950 p-6">
                            <FinderContent selected={selected} service={service} surveyHref={surveyHref} onReset={() => setSelected(null)} mobile />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* right — large dynamic service image (desktop/tablet) */}
          <div className="hidden lg:col-span-7 lg:block">
            <div className="relative h-full overflow-hidden border border-zinc-800" data-testid="finder-result">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selected ? selected.id : "empty"}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="relative aspect-[4/3]"
                >
                  <img
                    src={imgMeta.src}
                    alt={imgMeta.alt}
                    {...imgProps(imgMeta.src)}
                    loading="lazy" decoding="async"
                    style={{ objectPosition: imgMeta.pos }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {/* subtle dark gradient for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-charcoal/5" aria-hidden="true" />
                  <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
                    <FinderContent selected={selected} service={service} surveyHref={surveyHref} onReset={() => setSelected(null)} />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
